<template>
    <Head title="Create PM Plan" />

    <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!-- Header -->
        <div class="mb-6">
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                    <div>
                        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Create PM Plan</h1>
                        <p class="mt-1 text-sm text-gray-600 dark:text-secondary-400">Create a new preventive maintenance plan template</p>
                    </div>
                    <InfoButton title="Creating PM Plans" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                            PM Plans are reusable preventive maintenance templates that define recurring tasks, schedules,
                            and work order generation rules. Once created, these plans can be linked to multiple assets to
                            automatically create PM schedules and generate work orders at specified intervals. This ensures
                            consistent maintenance procedures across similar equipment and reduces setup time.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                            • <strong>Plan Name:</strong> Use descriptive, generic names like "Monthly HVAC Filter Check"
                              rather than asset-specific names<br>
                            • <strong>Description:</strong> Explain the purpose and scope of the maintenance work to provide
                              context for technicians<br>
                            • <strong>Frequency:</strong> Set how often maintenance should occur (e.g., 1 month, 3 months,
                              90 days, 1 year)<br>
                            • <strong>Priority:</strong> Determines urgency of auto-generated work orders - Urgent for
                              critical systems, High for important equipment, Normal for routine maintenance, Low for
                              optional tasks<br>
                            • <strong>Work Order Templates:</strong> Use placeholders {plan_name} and {asset_name} which
                              auto-fill when work orders are created. Example: "{plan_name} for {asset_name}" becomes
                              "Monthly HVAC Check for Chiller Unit 3"<br>
                            • <strong>Estimated Hours/Cost:</strong> Optional fields that help with resource planning and
                              budgeting<br>
                            • <strong>PM Tasks:</strong> Add step-by-step checklist tasks that technicians must complete.
                              Tasks appear on generated work orders as a checklist<br>
                            • <strong>Status:</strong> Check "Activate immediately" to start using this plan right away,
                              or leave unchecked to save as draft
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                            • <strong>Generic not specific</strong> - Design plans for equipment types, not individual
                              assets, so they can be reused<br>
                            • <strong>Clear task descriptions</strong> - Write tasks as if instructing someone unfamiliar
                              with the procedure<br>
                            • <strong>Use placeholders wisely</strong> - {plan_name} and {asset_name} make work orders
                              more specific automatically<br>
                            • <strong>After creation</strong> - Link this plan to assets via Settings → Maintenance →
                              Assets → Select Asset → PM Schedule tab<br>
                            • <strong>Frequency planning</strong> - Use months for most equipment (1, 3, 6, 12), weeks
                              for frequent tasks, years for annual certifications<br>
                            • <strong>Priority matters</strong> - Sets the default priority for all work orders generated
                              from this plan<br>
                            • <strong>Test first</strong> - Create plan, link to one test asset, verify work order
                              generation before rolling out to all assets
                        </p>
                    </InfoButton>
                </div>
                <Link
                    :href="route('settings.maintenance.pm-plans.index')"
                    class="inline-flex items-center px-4 py-2 bg-white dark:bg-secondary-700 border border-gray-300 dark:border-secondary-600 rounded-md font-semibold text-xs text-gray-700 dark:text-secondary-300 uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-secondary-600"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back
                </Link>
            </div>
        </div>

        <!-- Form -->
        <form @submit.prevent="submit" class="bg-white dark:bg-secondary-800 rounded-lg shadow">
            <div class="p-6 space-y-8">
                <!-- Basic Information -->
                <div>
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Basic Information</h3>
                    <div class="grid grid-cols-1 gap-6">
                        <!-- Plan Name -->
                        <div>
                            <label for="name" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                Plan Name <span class="text-red-500">*</span>
                            </label>
                            <input
                                id="name"
                                v-model="form.name"
                                type="text"
                                required
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.name }"
                                placeholder="e.g., Monthly HVAC Inspection"
                            />
                            <p v-if="form.errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.name }}</p>
                        </div>

                        <!-- Description -->
                        <div>
                            <label for="description" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                Description
                            </label>
                            <textarea
                                id="description"
                                v-model="form.description"
                                rows="3"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.description }"
                                placeholder="Describe the purpose and scope of this PM plan"
                            ></textarea>
                            <p v-if="form.errors.description" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.description }}</p>
                        </div>
                    </div>
                </div>

                <!-- Schedule Configuration -->
                <div>
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Schedule Configuration</h3>
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <!-- Frequency Value -->
                        <div>
                            <label for="frequency_value" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                Frequency <span class="text-red-500">*</span>
                            </label>
                            <input
                                id="frequency_value"
                                v-model="form.frequency_value"
                                type="number"
                                min="1"
                                required
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.frequency_value }"
                                placeholder="e.g., 1, 3, 6"
                            />
                            <p v-if="form.errors.frequency_value" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.frequency_value }}</p>
                        </div>

                        <!-- Frequency Unit -->
                        <div>
                            <label for="frequency_unit" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                Period <span class="text-red-500">*</span>
                            </label>
                            <select
                                id="frequency_unit"
                                v-model="form.frequency_unit"
                                required
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.frequency_unit }"
                            >
                                <option value="">Select Period</option>
                                <option value="days">Days</option>
                                <option value="weeks">Weeks</option>
                                <option value="months">Months</option>
                                <option value="years">Years</option>
                            </select>
                            <p v-if="form.errors.frequency_unit" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.frequency_unit }}</p>
                        </div>

                        <!-- Priority -->
                        <div>
                            <label for="priority" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                Priority <span class="text-red-500">*</span>
                            </label>
                            <select
                                id="priority"
                                v-model="form.priority"
                                required
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.priority }"
                            >
                                <option value="">Select Priority</option>
                                <option value="low">Low</option>
                                <option value="normal">Normal</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                            <p v-if="form.errors.priority" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.priority }}</p>
                        </div>
                    </div>
                </div>

                <!-- Work Order Template -->
                <div>
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Work Order Template</h3>
                    <div class="space-y-6">
                        <!-- Work Order Title Template -->
                        <div>
                            <label for="work_order_title_template" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                Work Order Title Template <span class="text-red-500">*</span>
                            </label>
                            <input
                                id="work_order_title_template"
                                v-model="form.work_order_title_template"
                                type="text"
                                required
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.work_order_title_template }"
                                placeholder="e.g., {plan_name} for {asset_name}"
                            />
                            <p class="mt-1 text-xs text-gray-500 dark:text-secondary-400">Use {plan_name} and {asset_name} as placeholders</p>
                            <p v-if="form.errors.work_order_title_template" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.work_order_title_template }}</p>
                        </div>

                        <!-- Work Order Description Template -->
                        <div>
                            <label for="work_order_description_template" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                Work Order Description Template <span class="text-red-500">*</span>
                            </label>
                            <textarea
                                id="work_order_description_template"
                                v-model="form.work_order_description_template"
                                rows="4"
                                required
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.work_order_description_template }"
                                placeholder="Describe the work to be performed. Use {plan_name} and {asset_name} as placeholders."
                            ></textarea>
                            <p class="mt-1 text-xs text-gray-500 dark:text-secondary-400">Use {plan_name} and {asset_name} as placeholders</p>
                            <p v-if="form.errors.work_order_description_template" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.work_order_description_template }}</p>
                        </div>
                    </div>
                </div>

                <!-- Estimation -->
                <div>
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Estimation (Optional)</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Estimated Hours -->
                        <div>
                            <label for="estimated_hours" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                Estimated Hours
                            </label>
                            <input
                                id="estimated_hours"
                                v-model="form.estimated_hours"
                                type="number"
                                min="0"
                                step="0.5"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                :class="{ 'border-red-500': form.errors.estimated_hours }"
                                placeholder="0.0"
                            />
                            <p v-if="form.errors.estimated_hours" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.estimated_hours }}</p>
                        </div>

                        <!-- Estimated Cost -->
                        <div>
                            <label for="estimated_cost" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                Estimated Cost
                            </label>
                            <div class="mt-1 relative rounded-md shadow-sm">
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span class="text-gray-500 dark:text-secondary-400 sm:text-sm">{{ currencySymbol }}</span>
                                </div>
                                <input
                                    id="estimated_cost"
                                    v-model="form.estimated_cost"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    class="block w-full pl-7 rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    :class="{ 'border-red-500': form.errors.estimated_cost }"
                                    placeholder="0.00"
                                />
                            </div>
                            <p v-if="form.errors.estimated_cost" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.estimated_cost }}</p>
                        </div>
                    </div>
                </div>

                <!-- PM Tasks -->
                <div>
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white">PM Tasks</h3>
                        <button
                            type="button"
                            @click="addTask"
                            class="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50"
                        >
                            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Add Task
                        </button>
                    </div>

                    <!-- Tasks List -->
                    <div v-if="tasks.length > 0" class="space-y-3">
                        <div
                            v-for="(task, index) in tasks"
                            :key="index"
                            class="flex items-start space-x-3 p-4 bg-gray-50 dark:bg-secondary-750 rounded-lg"
                        >
                            <div class="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-700 dark:text-blue-400 font-medium text-sm">
                                {{ index + 1 }}
                            </div>
                            <div class="flex-1">
                                <textarea
                                    v-model="task.description"
                                    rows="2"
                                    class="block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                    :class="{ 'border-red-500': task.error }"
                                    placeholder="Describe the task to be performed..."
                                    @input="task.error = false"
                                ></textarea>
                                <p v-if="task.error" class="mt-1 text-sm text-red-600 dark:text-red-400">Task description is required</p>
                            </div>
                            <button
                                type="button"
                                @click="removeTask(index)"
                                class="flex-shrink-0 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                            >
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <!-- Empty State -->
                    <div v-else class="text-center py-8 bg-gray-50 dark:bg-secondary-750 rounded-lg border-2 border-dashed border-gray-300 dark:border-secondary-600">
                        <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                        <p class="mt-2 text-sm text-gray-500 dark:text-secondary-400">No tasks added yet</p>
                        <p class="text-xs text-gray-400 dark:text-secondary-500">Click "Add Task" to create a checklist for this PM plan</p>
                    </div>
                </div>

                <!-- Status -->
                <div>
                    <div class="flex items-center">
                        <input
                            id="is_active"
                            v-model="form.is_active"
                            type="checkbox"
                            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-secondary-600 rounded"
                        />
                        <label for="is_active" class="ml-2 block text-sm text-gray-900 dark:text-white">
                            Activate this PM plan immediately
                        </label>
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <div class="bg-gray-50 dark:bg-secondary-750 px-6 py-4 flex items-center justify-end space-x-3">
                <Link
                    :href="route('settings.maintenance.pm-plans.index')"
                    class="inline-flex items-center px-4 py-2 bg-white dark:bg-secondary-700 border border-gray-300 dark:border-secondary-600 rounded-md font-semibold text-xs text-gray-700 dark:text-secondary-300 uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-secondary-600"
                >
                    Cancel
                </Link>
                <button
                    type="submit"
                    :disabled="form.processing"
                    class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 disabled:opacity-50"
                >
                    <svg v-if="form.processing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Create PM Plan
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';
import { useCurrency } from '@/Composables/useCurrency';

const toast = useToast();
const { currencySymbol } = useCurrency();

const form = useSecureForm({
    name: '',
    description: '',
    frequency_type: 'time_based',
    frequency_value: '',
    frequency_unit: '',
    priority: 'normal',
    estimated_hours: '',
    estimated_cost: '',
    work_order_title_template: '{plan_name} for {asset_name}',
    work_order_description_template: 'Perform scheduled maintenance: {plan_name} on {asset_name}',
    required_parts: [],
    is_active: true,
    tasks: [],
});

const tasks = ref([]);

const addTask = () => {
    tasks.value.push({
        description: '',
        task_sequence: tasks.value.length + 1,
        error: false,
    });
};

const removeTask = (index) => {
    tasks.value.splice(index, 1);
    // Renumber task sequences
    tasks.value.forEach((task, idx) => {
        task.task_sequence = idx + 1;
    });
};

const validateTasks = () => {
    let valid = true;
    tasks.value.forEach(task => {
        if (!task.description.trim()) {
            task.error = true;
            valid = false;
        }
    });
    return valid;
};

const submit = () => {
    // Validate tasks
    if (tasks.value.length > 0 && !validateTasks()) {
        toast.error('Please fill in all task descriptions');
        return;
    }

    // Prepare tasks for submission
    form.tasks = tasks.value.map(task => ({
        description: task.description,
        task_sequence: task.task_sequence,
    }));

    form.post(route('settings.maintenance.pm-plans.store'), {
        onError: () => {
            toast.error('Failed to create PM plan. Please check the form.');
        },
    });
};
</script>
